import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, Search, MessageCircle, Heart, Crown, Video } from 'lucide-react';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      category: "Getting Started",
      icon: <Search className="w-5 h-5" />,
      questions: [
        {
          q: "What are the different user types on Unity Love?",
          a: "Unity Love supports three user types: Couples (established partnerships seeking single women), Single Women (interested in joining couples), and Poly Singles (individuals looking to start their own polyamorous families). Each type has different discovery and matching rules."
        },
        {
          q: "How do I create a profile?",
          a: "After registration, you'll be guided through profile setup. Couples provide 2 names and ages, while singles provide 1 name and age. Include your location, cultural background, interests, preferences, and what you're looking for in connections."
        },
        {
          q: "Is Unity Love free to use?",
          a: "Yes! Core features including messaging, video chat, Quick Match, and flirts are completely free with daily limits. Unity Elite ($19.99/month) adds premium features like seeing who liked you, advanced filters, higher limits, and priority placement."
        }
      ]
    },
    {
      category: "Matching & Discovery",
      icon: <Heart className="w-5 h-5" />,
      questions: [
        {
          q: "How does the Quick Match timer work?",
          a: "You have 10 seconds to decide on each profile. The timer creates urgency and encourages authentic, gut-level decisions. If you don't choose, the profile auto-advances to the next one."
        },
        {
          q: "Who can see my profile?",
          a: "Couples see single women and poly singles. Single women can see men, women, and couples. Poly singles see couples and other poly singles. This visibility system maximizes meaningful connections while respecting your preferences."
        },
        {
          q: "What's the difference between liking and flirting?",
          a: "Likes are for mutual matching - both users must like each other to match and message. Flirts are attention-getters you can send without matching, with 4 types: wink, wave, heart eyes, and fire. They help you stand out!"
        }
      ]
    },
    {
      category: "Messaging & Video Chat",
      icon: <Video className="w-5 h-5" />,
      questions: [
        {
          q: "When can I start messaging someone?",
          a: "Messaging is available immediately after you both like each other (mutual match). Core messaging features are free with generous daily limits."
        },
        {
          q: "How does video chat work?",
          a: "Video chat unlocks after exchanging 10+ messages with a match. Either person can send a video invitation, which the other can accept or decline. This ensures you've built some rapport before face-to-face interaction."
        },
        {
          q: "Is video chat safe?",
          a: "Yes! Video chat includes safety reminders, and you control when to enable it. Always meet people in public first, trust your instincts, and never share personal information like your address during video calls."
        }
      ]
    },
    {
      category: "Premium Features",
      icon: <Crown className="w-5 h-5" />,
      questions: [
        {
          q: "What does Unity Elite include?",
          a: "Unity Elite ($19.99/month) includes: profile verification badge, see who liked/flirted with you, advanced search filters, rewind feature (undo passes), incognito browsing mode, and priority placement in discovery."
        },
        {
          q: "Can I cancel my subscription anytime?",
          a: "Yes, you can cancel anytime. Your premium features remain active until the end of your billing period, then you'll return to the free tier with all core features still available."
        },
        {
          q: "Do I lose my matches if I downgrade?",
          a: "No! All your matches, messages, and connections remain intact. You'll only lose access to premium-specific features like seeing who liked you and advanced filters."
        }
      ]
    },
    {
      category: "Safety & Community",
      icon: <MessageCircle className="w-5 h-5" />,
      questions: [
        {
          q: "How do I report someone?",
          a: "Use the report button on any profile or message. You can also block users who make you uncomfortable. Our moderation team reviews all reports within 24 hours and takes appropriate action."
        },
        {
          q: "What are the community guidelines?",
          a: "We follow the L.O.V.E.R.S. framework: Learn, Open communication, Verbal consent, Expectations, Respectful behavior, and Safer spaces. See our full Community Guidelines page for details."
        },
        {
          q: "Is my data secure?",
          a: "Yes! We use industry-standard encryption, secure password hashing, and comply with GDPR and CCPA privacy laws. We never sell your data, and you control your privacy settings."
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenFAQ(openFAQ === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Navigation */}
      <nav className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-b border-gray-200 dark:border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <HelpCircle className="w-6 h-6 text-rose-600" />
              <span className="font-playfair text-xl font-bold text-gray-900 dark:text-white">
                Help Center
              </span>
            </div>

            <div className="w-20"></div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl font-bold text-gray-900 dark:text-white mb-4">
            How Can We Help?
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Find answers to common questions about Unity Love
          </p>
        </div>

        {/* Contact Support */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 mb-8 border border-gray-200 dark:border-slate-700">
          <div className="text-center">
            <h2 className="font-playfair text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Need More Help?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Can't find what you're looking for? Our support team is here to help!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="btn-primary py-2 px-6 text-center"
                type="button"
              >
                Contact Support
              </button>
              <button
                onClick={() => navigate('/contact?mode=safety')}
                className="btn-secondary py-2 px-6 text-center"
                type="button"
              >
                Safety Concerns
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-gray-200 dark:border-slate-700">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-rose-100 dark:bg-rose-500/20 rounded-full flex items-center justify-center text-rose-600">
                  {category.icon}
                </div>
                <h2 className="font-playfair text-2xl font-semibold text-gray-900 dark:text-white">
                  {category.category}
                </h2>
              </div>

              <div className="space-y-4">
                {category.questions.map((faq, questionIndex) => {
                  const isOpen = openFAQ === `${categoryIndex}-${questionIndex}`;
                  return (
                    <div key={questionIndex} className="border border-gray-200 dark:border-slate-600 rounded-lg">
                      <button
                        onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                        className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <span className="font-semibold text-gray-900 dark:text-white pr-4">
                          {faq.q}
                        </span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      
                      {isOpen && (
                        <div className="p-4 pt-0 border-t border-gray-100 dark:border-slate-600">
                          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            {faq.a}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Quick Links */}
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          <button
            onClick={() => navigate('/education/love-your-way')}
            className="p-4 bg-gradient-primary text-white rounded-lg hover:shadow-lg transition-all text-center"
          >
            <div className="font-semibold mb-1">💜 Love Your Way</div>
            <div className="text-sm text-white/90">Polyamory & Polygamy Guide</div>
          </button>

          <button
            onClick={() => navigate('/safety')}
            className="p-4 surface-card rounded-lg border border-gray-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-500 transition-colors text-center"
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Safety Center</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Safety tips and resources</div>
          </button>
          
          <button
            onClick={() => navigate('/guidelines')}
            className="p-4 surface-card rounded-lg border border-gray-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-500 transition-colors text-center"
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Guidelines</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Community standards</div>
          </button>
          
          <button
            onClick={() => navigate('/privacy')}
            className="p-4 surface-card rounded-lg border border-gray-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-500 transition-colors text-center"
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Privacy Policy</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">How we protect your data</div>
          </button>
          
          <button
            onClick={() => navigate('/terms')}
            className="p-4 surface-card rounded-lg border border-gray-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-500 transition-colors text-center"
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Terms</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Platform rules and policies</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;